import { RefObject } from 'react';

interface DownloadFileProps {
  data?: any; // for xlsx download
  fileUrl?: string;
  fileName: string;
  type: 'xlsx' | 'image';
}

export const downloadFileFromUrl = (
  url: string,
  fileName: string,
  openInNewTab?: boolean
) => {
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.display = 'none';
  if (openInNewTab) {
    link.target = '_blank';
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadFile = async (params: DownloadFileProps) => {
  let url = params.fileUrl || '';
  if (params.type === 'xlsx' && params.data)
    url = window.URL.createObjectURL(new Blob([params.data]));
  downloadFileFromUrl(url, params.fileName);
};

export const base64ToBlob = (base64: string, mime: string) => {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mime });
};

/**
 * Fetches a document URL and returns a displayable URL.
 * Handles cases where the URL returns JSON with base64Data instead of direct file content.
 * Falls back to original URL if fetch fails (e.g., CORS issues).
 */
export const getDocumentDisplayUrl = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);
    const contentType = response.headers.get('content-type') || '';

    // Check if response might be JSON (either application/json or text/plain with JSON content)
    if (
      contentType.includes('application/json') ||
      contentType.includes('text/plain')
    ) {
      const text = await response.text();

      // Try to parse as JSON
      try {
        const jsonData = JSON.parse(text);
        if (jsonData.base64Data) {
          // Determine mime type from the JSON or use a default
          const mimeType = jsonData.type || jsonData.mimeType || 'image/png';
          const blob = base64ToBlob(jsonData.base64Data, mimeType);
          return window.URL.createObjectURL(blob);
        }
      } catch {
        // Not valid JSON - fall back to original URL
        return url;
      }
      // JSON but no base64Data - fall back to original URL
      return url;
    }

    // Direct file content - create blob URL
    const blob = await response.blob();
    return window.URL.createObjectURL(blob);
  } catch (error) {
    // CORS or network error - fall back to original URL
    // This allows direct image URLs to still work in img tags
    console.error('Failed to fetch document, using original URL:', error);
    return url;
  }
};

export const downloadFileFromData = (data: any, fileName: string) => {
  const blob = base64ToBlob(data, 'application/pdf'); // Assuming the file is a PDF
  const url = window.URL.createObjectURL(blob);
  downloadFileFromUrl(url, fileName);
};

export const downloadQRCode = (
  canvasRef: RefObject<HTMLDivElement>,
  fileName: string
) => {
  const canvas = canvasRef?.current?.querySelector('canvas');
  const pngUrl = canvas?.toDataURL('image/png');
  const link = document.createElement('a');
  link.href = pngUrl ?? '';
  link.download = `${fileName.replaceAll(' ', '-')}.png`;
  link.click();
};

export const downloadFileFromUrlUsingBlob = async (
  url: string,
  fileName?: string
) => {
  try {
    const response = await fetch(url);
    const contentType = response.headers.get('content-type') || '';

    let blob: Blob;
    let derivedFileName = fileName;

    // Check if response might be JSON with base64 data
    if (
      contentType.includes('application/json') ||
      contentType.includes('text/plain')
    ) {
      const text = await response.text();

      try {
        const jsonData = JSON.parse(text);
        if (jsonData.base64Data) {
          // Extract mime type and create blob from base64
          const mimeType = jsonData.type || jsonData.mimeType || 'image/png';
          blob = base64ToBlob(jsonData.base64Data, mimeType);

          // Use name from JSON if available and no fileName provided
          if (!derivedFileName && jsonData.name) {
            derivedFileName = jsonData.name;
          }
        } else {
          // JSON but no base64Data - can't download
          console.error('Download failed: No base64Data in response');
          return;
        }
      } catch {
        // Not valid JSON - can't download
        console.error('Download failed: Invalid JSON response');
        return;
      }
    } else {
      // Direct file content
      blob = await response.blob();
    }

    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;

    // Derive file name if not provided
    const finalFileName =
      derivedFileName ||
      decodeURIComponent(url.split('/').pop()?.split('?')[0] || 'download');

    link.download = finalFileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
  }
};
